/* @jsx h */
import { h, ref, computed, defineComponent, PropType, watch } from "vue";
import {
  PaymentIntegration,
  CreatePaymentIntegrationData,
  UpdatePaymentIntegrationData,
  PaymentIntegrationType,
  PaymentIntegrationTypeSchema,
  PaymentIntegrationUsageType,
  PaymentIntegrationUsageTypeSchema,
} from "@lemmonhq/types";
import { useTrpc } from "@app/vue/providers/TrpcProvider";
import { useAuth } from "@app/vue/providers/Auth";
import { PaymentFormData } from "./types";
import { getIntegrationIcon, getIntegrationLabel, getIntegrationLogo, getIntegrationUsageLabel } from "./utils";
import { RightSheet } from "@lemmonhq/uikit";

export const PaymentIntegrationForm = defineComponent({
  name: "PaymentIntegrationForm",
  props: {
    mode: {
      type: String as PropType<"create" | "edit">,
      required: true,
    },
    integration: {
      type: Object as PropType<PaymentIntegration | null>,
      default: null,
    },
    onSubmit: {
      type: Function as PropType<() => void>,
      required: true,
    },
    onDelete: {
      type: Function as PropType<() => void>,
      required: true,
    },
    onClose: {
      type: Function as PropType<() => void>,
      required: true,
    },
  },
  setup(props) {
    const trpc = useTrpc();
    const auth = useAuth();

    const formData = ref<PaymentFormData>({
      type: props.integration?.type || "payrexx",
      credentials: props.integration?.credentials || {},
      settings: {
        type:
          (props.integration?.settings as { type?: PaymentIntegrationUsageType })
            ?.type || "terminal",
        ...(props.integration?.settings || {}),
      },
    });

    // Selected user IDs for the integration
    const selectedUserIds = ref<number[]>(props.integration?.user_ids ?? []);

    const submitting = ref(false);
    const formError = ref<string | null>(null);

    // Watch for changes in integration prop and update form data
    watch(
      () => props.integration,
      (newIntegration) => {
        if (newIntegration) {
          // Editing existing integration
          formData.value = {
            type: newIntegration.type,
            credentials: newIntegration.credentials || {},
            settings: {
              type:
                (newIntegration.settings as {
                  type?: PaymentIntegrationUsageType;
                })?.type || "terminal",
              ...(newIntegration.settings || {}),
            },
          };
          selectedUserIds.value = newIntegration.user_ids ?? [];
        } else {
          // Creating new integration
          formData.value = {
            type: "payrexx",
            credentials: {},
            settings: { type: "terminal" },
          };
          selectedUserIds.value = [];
        }
        formError.value = null;
      },
      { immediate: true },
    );

    // Get available users from auth
    const availableUsers = computed(() => {
      if (!auth.user.value) return [];

      // Combine current user with colleagues
      const users = [
        {
          id: auth.user.value.profile.id,
          name: auth.user.value.profile.name,
        },
        ...auth.user.value.colleagues
          .filter(
            (el) => el.profile.status === "active" && !el.profile.deleted_at,
          )
          .map((c) => ({
            id: c.profile.id,
            name: c.profile.name,
          })),
      ];

      // Remove duplicates based on id
      return users.filter(
        (user, index, self) =>
          index === self.findIndex((u) => u.id === user.id),
      );
    });

    // Get available integration types from Zod schema
    const availableIntegrationTypes = PaymentIntegrationTypeSchema.options;
    const availableUsageTypes = PaymentIntegrationUsageTypeSchema.options;

    // Type-specific form fields
    const payrexxFields = computed(() => ({
      instance_name: formData.value.credentials?.instance_name || "",
      api_key: formData.value.credentials?.api_key || "",
      is_external: formData.value.settings?.is_external || false,
    }));

    const integrationUsage = computed(
      () =>
        (formData.value.settings?.type as PaymentIntegrationUsageType | undefined) ||
        "terminal",
    );

    const stripeFields = computed(() => ({
      api_key: formData.value.credentials?.api_key || "",
    }));

    const nakkaFields = computed(() => ({
      password: formData.value.credentials?.password || "",
      name: formData.value.settings?.name || "",
    }));

    const tiblisiBankFields = computed(() => ({
      merchant_id: formData.value.credentials?.merchant_id || "",
      signature:
        formData.value.credentials?.signature ||
        formData.value.credentials?.api_key ||
        "",
    }));

    const walleeFields = computed(() => ({
      user_id: formData.value.credentials?.user_id || "",
      api_key: formData.value.credentials?.api_key || "",
      api_secret: formData.value.credentials?.api_secret || "",
      space_id: formData.value.credentials?.space_id || "",
      terminal_id: formData.value.credentials?.terminal_id || "",
    }));

    const handleSubmit = async (event: Event) => {
      event.preventDefault();
      try {
        submitting.value = true;
        formError.value = null;

        // Build credentials and settings based on type
        let credentials: any = {};
        let settings: Record<string, unknown> = {
          type: integrationUsage.value,
        };

        switch (formData.value.type) {
          case "payrexx":
            credentials = {
              instance_name: payrexxFields.value.instance_name,
              api_key: payrexxFields.value.api_key,
            };
            settings = {
              ...settings,
              is_external: payrexxFields.value.is_external,
            };
            break;
          case "stripe":
            credentials = {
              api_key: stripeFields.value.api_key,
            };
            break;
          case "nakka":
            credentials = {
              password: nakkaFields.value.password,
            };
            settings = {
              ...settings,
              name: nakkaFields.value.name,
            };
            break;
          case "tiblisi_bank":
            credentials = {
              merchant_id: tiblisiBankFields.value.merchant_id,
              signature: tiblisiBankFields.value.signature,
              api_key: tiblisiBankFields.value.signature || undefined,
            };
            break;
          case "payrexx_softpos":
            credentials = {};
            break;
          case "wallee":
            credentials = {
              user_id: walleeFields.value.user_id,
              api_key: walleeFields.value.api_key || undefined,
              api_secret: walleeFields.value.api_secret,
              space_id: walleeFields.value.space_id,
              terminal_id: walleeFields.value.terminal_id,
            };
            break;
        }

        if (props.mode === "create") {
          const createData: CreatePaymentIntegrationData = {
            type: formData.value.type,
            credentials,
            settings,
            user_ids: selectedUserIds.value,
          };
          await trpc.paymentIntegrations.create.mutate(createData);
        } else if (props.mode === "edit" && props.integration) {
          const updateData: UpdatePaymentIntegrationData = {
            type: formData.value.type,
            credentials,
            settings,
            user_ids: selectedUserIds.value,
          };
          await trpc.paymentIntegrations.update.mutate({
            id: props.integration.id,
            data: updateData,
          });
        }

        props.onSubmit();
      } catch (err) {
        formError.value =
          err instanceof Error
            ? err.message
            : "Failed to save payment integration";
        console.error("Failed to save payment integration:", err);
      } finally {
        submitting.value = false;
      }
    };

    const handleDelete = async () => {
      if (
        !props.integration ||
        !confirm("Are you sure you want to delete this payment integration?")
      ) {
        return;
      }

      try {
        submitting.value = true;
        formError.value = null;
        await trpc.paymentIntegrations.delete.mutate({
          id: props.integration.id,
        });
        props.onDelete();
      } catch (err) {
        formError.value =
          err instanceof Error
            ? err.message
            : "Failed to delete payment integration";
        console.error("Failed to delete payment integration:", err);
      } finally {
        submitting.value = false;
      }
    };

    return () => (
      <RightSheet
        title={
          props.mode === "create"
            ? "Add a new integration"
            : `Edit ${getIntegrationLabel(
                props.integration?.type || "payrexx",
              )}`
        }
        isOpen={true}
        onClose={props.onClose}
      >
        <form class="flex h-full flex-col" onSubmit={handleSubmit}>
          <div class="flex-1 space-y-6 overflow-y-auto px-6 py-6">
            {formError.value && (
              <div class="alert alert-error">
                <span class="material-symbols-outlined">error</span>
                <span>{formError.value}</span>
              </div>
            )}

            {/* Provider picker */}
            <div class="form-control">
              <label class="label">
                <span class="label-text text-sm font-medium text-base-content/80">
                  Provider
                </span>
              </label>
              <div class="grid grid-cols-3 gap-2">
                {availableIntegrationTypes.map((type) => {
                  const logo = getIntegrationLogo(type);
                  const isSelected = formData.value.type === type;
                  const isEdit = props.mode === "edit";
                  return (
                    <button
                      key={type}
                      type="button"
                      disabled={isEdit}
                      class={`flex flex-col items-center gap-2 rounded-xl border-2 p-3 transition-colors ${
                        isSelected
                          ? "border-primary bg-primary/5"
                          : " hover:border-base-content/30"
                      } ${isEdit && !isSelected ? "opacity-30" : ""}`}
                      onClick={() => {
                        formData.value = {
                          type,
                          credentials: {},
                          settings: { type: integrationUsage.value },
                        };
                      }}
                    >
                      {logo ? (
                        <div class="h-8 w-full flex items-center justify-center">
                          <img src={logo} alt={type} class="h-8 max-w-full object-contain" />
                        </div>
                      ) : (
                        <span class="material-symbols-outlined text-2xl text-primary">
                          {getIntegrationIcon(type)}
                        </span>
                      )}
                      <span class="text-xs font-medium text-center text-base-content/70 leading-tight">
                        {getIntegrationLabel(type)}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Usage */}
            <div class="form-control">
              <label class="label">
                <span class="label-text text-sm font-medium text-base-content/80">
                  Usage
                </span>
              </label>
              <select
                class="select select-bordered w-full"
                value={integrationUsage.value}
                onChange={(e: any) => {
                  formData.value.settings = {
                    ...formData.value.settings,
                    type: e.target.value as PaymentIntegrationUsageType,
                  };
                }}
              >
                {availableUsageTypes.map((usage) => (
                  <option key={usage} value={usage}>
                    {getIntegrationUsageLabel(usage)}
                  </option>
                ))}
              </select>
            </div>

            {/* Type-specific fields */}
            {formData.value.type === "payrexx" && (
              <div class="space-y-4">
                <div class="divider">Payrexx Configuration</div>
                <div class="form-control">
                  <label class="label">
                    <span class="label-text text-sm font-medium text-base-content/80">
                      Instance Name
                    </span>
                  </label>
                  <input
                    type="text"
                    class="input input-bordered"
                    value={payrexxFields.value.instance_name}
                    onInput={(e: any) => {
                      formData.value.credentials = {
                        ...formData.value.credentials,
                        instance_name: e.target.value,
                      };
                    }}
                    required
                    placeholder="your-instance"
                  />
                </div>
                <div class="form-control">
                  <label class="label">
                    <span class="label-text text-sm font-medium text-base-content/80">
                      API Key
                    </span>
                  </label>
                  <input
                    type="text"
                    class="input input-bordered font-mono"
                    value={payrexxFields.value.api_key}
                    onInput={(e: any) => {
                      formData.value.credentials = {
                        ...formData.value.credentials,
                        api_key: e.target.value,
                      };
                    }}
                    required
                    placeholder="pk_live_..."
                  />
                </div>
                <div class="form-control">
                  <label class="label cursor-pointer justify-start gap-4">
                    <input
                      type="checkbox"
                      class="toggle toggle-primary"
                      checked={payrexxFields.value.is_external}
                      onChange={(e: any) => {
                        formData.value.settings = {
                          ...formData.value.settings,
                          is_external: e.target.checked,
                        };
                      }}
                    />
                    <span class="label-text text-sm font-medium text-base-content/80">
                      External Integration
                    </span>
                  </label>
                </div>
              </div>
            )}

            {formData.value.type === "stripe" && (
              <div class="space-y-4">
                <div class="divider">Stripe Configuration</div>
                <div class="form-control">
                  <label class="label">
                    <span class="label-text text-sm font-medium text-base-content/80">
                      API Key
                    </span>
                  </label>
                  <input
                    type="text"
                    class="input input-bordered font-mono"
                    value={stripeFields.value.api_key}
                    onInput={(e: any) => {
                      formData.value.credentials = {
                        api_key: e.target.value,
                      };
                    }}
                    required
                    placeholder="sk_live_..."
                  />
                </div>
              </div>
            )}

            {formData.value.type === "nakka" && (
              <div class="space-y-4">
                <div class="divider">Nakka Configuration</div>
                <div class="form-control">
                  <label class="label">
                    <span class="label-text text-sm font-medium text-base-content/80">
                      Name
                    </span>
                  </label>
                  <input
                    type="text"
                    class="input input-bordered"
                    value={nakkaFields.value.name}
                    onInput={(e: any) => {
                      formData.value.settings = {
                        ...formData.value.settings,
                        name: e.target.value,
                      };
                    }}
                    required
                    placeholder="Integration name"
                  />
                </div>
                <div class="form-control">
                  <label class="label">
                    <span class="label-text text-sm font-medium text-base-content/80">
                      Password
                    </span>
                  </label>
                  <input
                    type="text"
                    class="input input-bordered font-mono"
                    value={nakkaFields.value.password}
                    onInput={(e: any) => {
                      formData.value.credentials = {
                        password: e.target.value,
                      };
                    }}
                    required
                    placeholder="password"
                  />
                </div>
              </div>
            )}

            {formData.value.type === "tiblisi_bank" && (
              <div class="space-y-4">
                <div class="divider">Tbilisi Bank Configuration</div>
                <div class="form-control">
                  <label class="label">
                    <span class="label-text text-sm font-medium text-base-content/80">
                      Merchant ID
                    </span>
                  </label>
                  <input
                    type="text"
                    class="input input-bordered font-mono"
                    value={tiblisiBankFields.value.merchant_id}
                    onInput={(e: any) => {
                      formData.value.credentials = {
                        ...formData.value.credentials,
                        merchant_id: e.target.value,
                      };
                    }}
                    required
                    placeholder="10000123"
                  />
                </div>
                <div class="form-control">
                  <label class="label">
                    <span class="label-text text-sm font-medium text-base-content/80">
                      Signature
                    </span>
                  </label>
                  <input
                    type="text"
                    class="input input-bordered font-mono"
                    value={tiblisiBankFields.value.signature}
                    onInput={(e: any) => {
                      formData.value.credentials = {
                        ...formData.value.credentials,
                        signature: e.target.value,
                        api_key: e.target.value,
                      };
                    }}
                    required
                    placeholder="signature"
                  />
                </div>
              </div>
            )}

            {formData.value.type === "payrexx_softpos" && (
              <div class="space-y-4">
                <div class="divider">Payrexx SoftPOS Configuration</div>
              </div>
            )}

            {formData.value.type === "wallee" && (
              <div class="space-y-4">
                <div class="divider">Wallee Configuration</div>
                <div class="form-control">
                  <label class="label">
                    <span class="label-text text-sm font-medium text-base-content/80">
                      User ID
                    </span>
                  </label>
                  <input
                    type="text"
                    class="input input-bordered font-mono"
                    value={walleeFields.value.user_id}
                    onInput={(e: any) => {
                      formData.value.credentials = {
                        ...formData.value.credentials,
                        user_id: e.target.value,
                      };
                    }}
                    required
                    placeholder="12345"
                  />
                </div>
                <div class="form-control">
                  <label class="label">
                    <span class="label-text text-sm font-medium text-base-content/80">
                      API Key
                    </span>
                  </label>
                  <input
                    type="text"
                    class="input input-bordered font-mono"
                    value={walleeFields.value.api_key}
                    onInput={(e: any) => {
                      formData.value.credentials = {
                        ...formData.value.credentials,
                        api_key: e.target.value,
                      };
                    }}
                    placeholder="base64-auth-key"
                  />
                </div>
                <div class="form-control">
                  <label class="label">
                    <span class="label-text text-sm font-medium text-base-content/80">
                      API Secret
                    </span>
                  </label>
                  <input
                    type="text"
                    class="input input-bordered font-mono"
                    value={walleeFields.value.api_secret}
                    onInput={(e: any) => {
                      formData.value.credentials = {
                        ...formData.value.credentials,
                        api_secret: e.target.value,
                      };
                    }}
                    placeholder="api-secret"
                  />
                </div>
                <div class="form-control">
                  <label class="label">
                    <span class="label-text text-sm font-medium text-base-content/80">
                      Space ID
                    </span>
                  </label>
                  <input
                    type="text"
                    class="input input-bordered font-mono"
                    value={walleeFields.value.space_id}
                    onInput={(e: any) => {
                      formData.value.credentials = {
                        ...formData.value.credentials,
                        space_id: e.target.value,
                      };
                    }}
                    required
                    placeholder="67890"
                  />
                </div>
                <div class="form-control">
                  <label class="label">
                    <span class="label-text text-sm font-medium text-base-content/80">
                      Terminal ID
                    </span>
                  </label>
                  <input
                    type="text"
                    class="input input-bordered font-mono"
                    value={walleeFields.value.terminal_id}
                    onInput={(e: any) => {
                      formData.value.credentials = {
                        ...formData.value.credentials,
                        terminal_id: e.target.value,
                      };
                    }}
                    required
                    placeholder="301268"
                  />
                </div>
              </div>
            )}

            {/* User Assignment */}
            <div class="form-control">
              <label class="label">
                <span class="label-text text-sm font-medium text-base-content/80">
                  Assign to Users (Optional)
                </span>
              </label>
              <div class="space-y-2">
                {availableUsers.value.map((user) => (
                  <label
                    key={user.id}
                    class="label cursor-pointer justify-start gap-4 hover:bg-base-200 rounded-lg px-2 py-1"
                  >
                    <input
                      type="checkbox"
                      class="checkbox checkbox-primary checkbox-sm"
                      checked={selectedUserIds.value.includes(user.id)}
                      onChange={(e: any) => {
                        if (e.target.checked) {
                          selectedUserIds.value = [
                            ...selectedUserIds.value,
                            user.id,
                          ];
                        } else {
                          selectedUserIds.value = selectedUserIds.value.filter(
                            (id) => id !== user.id,
                          );
                        }
                      }}
                    />
                    <span class="label-text text-sm">{user.name}</span>
                  </label>
                ))}
                {availableUsers.value.length === 0 && (
                  <p class="text-sm text-base-content/60">No users available</p>
                )}
              </div>
              <label class="label">
                <span class="label-text-alt text-xs text-base-content/60">
                  Select users who can use this payment integration. Leave empty
                  to allow all users.
                </span>
              </label>
            </div>

            {props.mode === "edit" && (
              <div class="form-control">
                <label class="label">
                  <span class="label-text text-sm font-medium text-base-content/80">
                    Delete Integration
                  </span>
                </label>
                <div class="py-2">
                  <button
                    type="button"
                    class="btn btn-error"
                    onClick={handleDelete}
                    disabled={submitting.value}
                  >
                    {submitting.value ? "Deleting..." : "Delete"}
                  </button>
                </div>
              </div>
            )}
          </div>

          <div class="flex items-center justify-between gap-4 border-t  bg-base-200/40 px-6 py-4">
            <div class="text-xs text-base-content/60">
              Securely store your payment credentials.
            </div>
            <button
              type="submit"
              class="btn btn-primary"
              disabled={submitting.value}
            >
              {submitting.value ? (
                <>
                  <span class="loading loading-spinner loading-sm"></span>
                  {props.mode === "create" ? "Adding..." : "Saving..."}
                </>
              ) : (
                <>
                  <span class="material-symbols-outlined">
                    {props.mode === "create" ? "add" : "save"}
                  </span>
                  {props.mode === "create" ? "Add integration" : "Save changes"}
                </>
              )}
            </button>
          </div>
        </form>
      </RightSheet>
    );
  },
});
